import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react'

import {
  DateRangePicker,
  Dropdown,
  Icon,
  Link,
  LoadingSkeleton,
  Option,
  Text,
  TextButton,
  TextField,
  Tooltip,
} from '@joor/design-system'
import { useFlags } from '@joor/launchdarkly-react-client-sdk'
import { useSelector } from 'react-redux'
import styled from 'styled-components'

import { OrderStatus } from '__generated__/atlas-types'

import {
  ISO_DATE_TIME_FORMAT,
  getDateFromFormat,
  isDateBefore,
} from 'utils/dates'
import { formatNameWithCode } from 'utils/formatters'
import { fromGlobalId } from 'utils/transformations/graphql'

import { getFormattedCurrency } from 'shop/formatters'
import {
  getDateFormat,
  isAccountTypeBrand as isAccountTypeBrandSelector,
  isChangeOrderStartShipBeforeLinesheetRestricted as isChangeOrderStartShipBeforeLinesheetRestrictedSelector,
  isCreateCustomPoRestricted as isCreateCustomPoRestrictedSelector,
  isEditApprovedShippedRestricted as isEditApprovedShippedRestrictedSelector,
} from 'store/currentUser/selectors'
import { isChangingDeliveryDatesRestricted as isChangingDeliveryDatesRestrictedSelector } from 'store/currentUser/selectors'

import { EMPTY_CELL_CONTENT } from 'components/Core/Table/Cell'

import { ReviewPageIds } from '../../review.ids'
import { OrderData } from '../Overview/Overview'
import { TOOLTIP_CONTENT } from '../Overview/components/OrderDetailsWidget/components/OrderDetailsEditForm/DetailFormItem'
import { EditedFields } from './Header'
import { LinesheetsWrapper } from './ReadonlyHeaderDetails'
import DetailValue from './components/DetailValue/DetailValue'
import { usePriceTypeForm } from './header.hooks'
import * as UI from './header.ui'
import { getShippingDeliveryWindowErrorMessage } from './header.utils'

const OrderTitleWrapper = styled(UI.DetailTitle)`
  display: flex;
  gap: 5px;
  align-items: center;
`

const TitleWithTooltipWrapper = styled(UI.DetailTitle)`
  display: flex;
  gap: 5px;
  align-items: center;
`

const EditableRetailerWrapper = styled.div`
  display: flex; 
  gap: 5px;
  align-items: baseline;
  & > div {
    width: 30%;
  }
}
`

const StyledTextButton = styled(TextButton)`
  margin-top: 10px;
  padding-right: 20px;
`

export enum EditableHeaderTestIds {
  OrderNumberItem = 'header-order-number-item',
  OrderNumberInput = 'header-order-number-input',
  RetailerNameItem = 'header-retailer-name-item',
  RetailerCodeInput = 'header-retailer-code-input',
  PriceTypeItem = 'header-price-type-item',
  PriceTypeDropdown = 'header-price-type-dropdown',
  StatusItem = 'header-status-item',
  StatusDropdown = 'header-status-dropdown',
  LinesheetNameItem = 'header-linesheet-name-item',
}

export const DetailLoading = () => <LoadingSkeleton width="50%" height={10} />

const PriceTypeDetailItem = ({
  priceType,
  priceTypeId,
  isFetching,
  isAccountTypeBrand,
  priceTypeOptions,
  editedFields,
  setEditedFields,
  disableEditPriceType,
}: {
  priceType?: OrderData['priceType']
  priceTypeId?: string
  isFetching?: boolean
  isAccountTypeBrand: boolean
  priceTypeOptions: Option[]
  disableEditPriceType: boolean
  editedFields: EditedFields
  setEditedFields: Dispatch<SetStateAction<EditedFields>>
}) => {
  const [selectedPriceType, setSelectedPriceType] = useState<
    Option | undefined
  >(undefined)

  useEffect(() => {
    // doing this ugly useEffect because the pricetype is initialized as undefined
    if (priceTypeId) {
      setSelectedPriceType(
        priceTypeOptions.find((option) => option.value === priceTypeId),
      )
    }
  }, [priceTypeId])

  return (
    <div style={{ maxWidth: '250px', minWidth: '200px' }}>
      <UI.DetailTitle>Price Type</UI.DetailTitle>
      {isFetching ? (
        <DetailLoading />
      ) : (
        <Dropdown
          id={ReviewPageIds.PriceTypeDropdownId}
          testId={EditableHeaderTestIds.PriceTypeDropdown}
          value={selectedPriceType}
          placeholder={'Price type'}
          onSelect={({ selected }: { selected: Option }): void => {
            setSelectedPriceType(selected)
            setEditedFields({
              ...editedFields,
              priceType: selected.value,
            })
          }}
          disabled={disableEditPriceType}
          options={priceTypeOptions}
        >
          <DetailValue>
            {isAccountTypeBrand
              ? getFormattedCurrency(priceType)
              : // FIXME: remove the conditional chaining after review review.queries returning
                // empty object and after implementing the loading thing
                formatNameWithCode(
                  priceType?.currency.name,
                  priceType?.currency.code,
                )}
          </DetailValue>
        </Dropdown>
      )}
    </div>
  )
}

type Props = {
  order?: OrderData
  editedFields: EditedFields
  setEditedFields: Dispatch<SetStateAction<EditedFields>>
  isReadOnly?: boolean
  isFetching?: boolean
  onHandleSaveBtnClick: () => void
}

export const EditableHeaderDetails = ({
  order,
  editedFields,
  setEditedFields,
  isReadOnly,
  isFetching,
  onHandleSaveBtnClick,
}: Props) => {
  const isAccountTypeBrand = useSelector(isAccountTypeBrandSelector)
  const isEditApprovedShippedRestricted = useSelector(
    isEditApprovedShippedRestrictedSelector,
  )
  const isChangeOrderStartShipBeforeLinesheetRestricted = useSelector(
    isChangeOrderStartShipBeforeLinesheetRestrictedSelector,
  )
  const isChangingDeliveryDatesRestricted = useSelector(
    isChangingDeliveryDatesRestrictedSelector,
  )
  const dateFormat = useSelector(getDateFormat)
  const isCreateCustomPoRestricted = useSelector(
    isCreateCustomPoRestrictedSelector,
  )

  const { priceTypeOptions, priceTypeId, priceType } = usePriceTypeForm({
    orderId: order?.id,
    orderRetailerId: order?.retailerProfile?.id,
    orderPriceType: order?.priceType,
    isBrand: isAccountTypeBrand,
  })

  const [orderReferenceNumber, setOrderReferenceNumber] = useState('')

  const { newCollectionsEditAndCreate } = useFlags()

  const initializeState = useCallback((orderInput: typeof order) => {
    setOrderReferenceNumber(orderInput?.referenceNumber ?? '')
  }, [])

  useEffect(() => {
    if (order) {
      initializeState(order)
    }
  }, [order, initializeState])

  const applyItemClassNameSuffix = (suffix: 'sm' | 'md' | 'lg') =>
    isAccountTypeBrand ? suffix : 'default'

  const cannotEditPriceType =
    (isEditApprovedShippedRestricted &&
      order?.status === OrderStatus.APPROVED) ||
    order?.status === OrderStatus.CANCELLED ||
    order?.status === OrderStatus.SHIPPED

  const disableEditPriceType =
    cannotEditPriceType || !isAccountTypeBrand || isReadOnly

  const getLinesheetDetailsLink = (linesheetId: string) => {
    return newCollectionsEditAndCreate
      ? `${window.location.origin}/ra/linesheet/${fromGlobalId(linesheetId).id}`
      : `${window.location.origin}/collections/view/${
          fromGlobalId(linesheetId).id || ''
        }?brandId=${fromGlobalId(order?.brand?.id).id}`
  }

  const handleIsOutsideRange = (day: Date): boolean => {
    if (isChangeOrderStartShipBeforeLinesheetRestricted) {
      return isImmediate
        ? isDateBefore(day, new Date())
        : isDateBefore(
            day,
            order?.collections?.deliveryWindow?.minStartShipDate as string,
          )
    }

    return false
  }

  const isImmediate =
    !order?.collections?.deliveryWindow?.minStartShipDate &&
    !order?.collections?.deliveryWindow?.maxCompleteShipDate

  return (
    <UI.DetailsWrapper>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
        <UI.DetailItem
          className={`item-left item-${applyItemClassNameSuffix('sm')}`}
          data-testid={EditableHeaderTestIds.OrderNumberItem}
        >
          <OrderTitleWrapper>Order #</OrderTitleWrapper>
          {!orderReferenceNumber || isCreateCustomPoRestricted ? (
            <Text fontFamily="alpha" fontSize="base" fontWeight="medium">
              {orderReferenceNumber ?? undefined}
            </Text>
          ) : (
            <TextField
              id={ReviewPageIds.OrderReferenceInputId}
              defaultValue={orderReferenceNumber ?? undefined}
              data-testid={EditableHeaderTestIds.OrderNumberInput}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setEditedFields({
                  ...editedFields,
                  orderReferenceNumber: e.target.value,
                })
              }
            />
          )}
        </UI.DetailItem>
        <UI.DetailItem
          className={`inner-item item-${applyItemClassNameSuffix('md')}`}
          data-testid={EditableHeaderTestIds.RetailerNameItem}
        >
          <UI.DetailTitle>
            {isAccountTypeBrand ? (
              <TitleWithTooltipWrapper>
                {order?.retailerProfile?.code ? (
                  <>
                    Retailer{' '}
                    <Tooltip
                      content={'Retailer Code has already been entered.'}
                      defaultPlacement="top"
                    >
                      <Icon
                        iconName="information"
                        iconSize="small"
                        variant="ACTIVE"
                      />
                    </Tooltip>
                  </>
                ) : (
                  'Retailer'
                )}
              </TitleWithTooltipWrapper>
            ) : (
              'Brand'
            )}
          </UI.DetailTitle>
          {isFetching && <DetailLoading />}
          {!isFetching && isAccountTypeBrand && (
            <DetailValue>
              {order?.retailerProfile?.code ? (
                formatNameWithCode(
                  order?.retailerProfile?.name,
                  order?.retailerProfile?.code,
                )
              ) : (
                <EditableRetailerWrapper>
                  <p>{order?.retailerProfile?.name}</p>
                  <TextField
                    placeholder="Code"
                    data-testid={EditableHeaderTestIds.RetailerCodeInput}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setEditedFields({
                        ...editedFields,
                        retailerCode: e.target.value,
                      })
                    }
                  />
                </EditableRetailerWrapper>
              )}
            </DetailValue>
          )}
          {!isFetching && !isAccountTypeBrand && (
            <DetailValue>{order?.brand?.name ?? ''}</DetailValue>
          )}
        </UI.DetailItem>
        <UI.DetailItem
          className={`inner-item item-${applyItemClassNameSuffix('md')}`}
          data-testid={EditableHeaderTestIds.LinesheetNameItem}
        >
          <TitleWithTooltipWrapper>
            Linesheet{' '}
            <Tooltip content={TOOLTIP_CONTENT} defaultPlacement="top">
              <Icon iconName="information" iconSize="small" variant="ACTIVE" />
            </Tooltip>
          </TitleWithTooltipWrapper>
          <LinesheetsWrapper>
            {!isFetching && !order?.collections?.edges.length ? (
              EMPTY_CELL_CONTENT
            ) : (
              <DetailValue>
                {order?.collections?.edges.map((edge, index) => (
                  <div key={`linesheet-${index}`}>
                    <Link
                      data-testid={`linesheet-name-${edge?.node?.id}-link`}
                      key={edge?.node?.id}
                      target="_blank"
                      href={getLinesheetDetailsLink(edge?.node?.id || '')}
                      className="notranslate"
                    >
                      {formatNameWithCode(edge?.node?.name, edge?.node?.code)}
                    </Link>
                  </div>
                ))}
              </DetailValue>
            )}
          </LinesheetsWrapper>
        </UI.DetailItem>
        <UI.DetailItem
          className={`inner-item item-${applyItemClassNameSuffix('md')}`}
          data-testid={EditableHeaderTestIds.LinesheetNameItem}
        >
          <UI.DetailTitle>Shipping Delivery Window</UI.DetailTitle>
          <DateRangePicker
            startDateAriaLabel="Shipping Start date"
            endDateAriaLabel="Shipping End date"
            startDate={getDateFromFormat(
              order?.shipping?.deliveryWindow?.startDate,
              ISO_DATE_TIME_FORMAT,
              { utc: true },
            )}
            startDateId={`start-${order?.shipping?.deliveryWindow?.startDate}`}
            endDate={getDateFromFormat(
              order?.shipping?.deliveryWindow?.completeDate,
              ISO_DATE_TIME_FORMAT,
              { utc: true },
            )}
            endDateId={`end-${order?.shipping?.deliveryWindow?.completeDate}`}
            focusedInput={null}
            onDatesChange={({ endDate, startDate }) => {
              setEditedFields({
                ...editedFields,
                ...(startDate && { startDate }),
                ...(endDate && { endDate }),
              })
            }}
            onFocusChange={() => {}}
            numberOfMonths={1}
            errorMessage={
              getShippingDeliveryWindowErrorMessage(
                order?.shipping?.deliveryWindow?.startDate,
                order?.shipping?.deliveryWindow?.completeDate,
              ) || undefined
            }
            caption={
              getShippingDeliveryWindowErrorMessage(
                order?.shipping?.deliveryWindow?.startDate,
                order?.shipping?.deliveryWindow?.completeDate,
              ) || undefined
            }
            placeholder={dateFormat?.toUpperCase() ?? 'MM/DD/YYYY'}
            displayFormat={dateFormat?.toUpperCase() ?? 'MM/DD/YYYY'}
            disabled={!isAccountTypeBrand || isChangingDeliveryDatesRestricted}
            openDirection="down"
            isOutsideRange={handleIsOutsideRange}
            minimumNights={
              order?.collections?.edges[0]?.node.delivery
                ?.minimumShippingWindow || undefined
            }
          />
        </UI.DetailItem>
        <UI.DetailItem
          className={`inner-item item-${applyItemClassNameSuffix('sm')}`}
          data-testid={EditableHeaderTestIds.PriceTypeItem}
        >
          <PriceTypeDetailItem
            isFetching={isFetching}
            isAccountTypeBrand={isAccountTypeBrand}
            priceType={priceType}
            priceTypeId={priceTypeId}
            priceTypeOptions={priceTypeOptions}
            editedFields={editedFields}
            setEditedFields={setEditedFields}
            disableEditPriceType={disableEditPriceType}
          />
        </UI.DetailItem>
      </div>
      {order?.status &&
        isAccountTypeBrand &&
        (order?.isOrphan ? (
          <Tooltip
            content="You must assign a retailer to edit the header info"
            defaultPlacement={'left'}
          >
            <StyledTextButton onClick={onHandleSaveBtnClick} disabled={true}>
              Save
            </StyledTextButton>
          </Tooltip>
        ) : (
          <StyledTextButton
            onClick={onHandleSaveBtnClick}
            disabled={order.status === OrderStatus.CANCELLED}
          >
            Save
          </StyledTextButton>
        ))}
    </UI.DetailsWrapper>
  )
}
